import { CurvePath, LineCurve3, Vector3, CatmullRomCurve3 } from 'three'

export type LatLng = {
  lat: number
  lng: number
}

export function latLngToVector3({ lat, lng }: LatLng, radius: number) {
  // Convert degrees to radians
  const phi = ((90 - lat) * Math.PI) / 180
  const theta = ((lng + 180) * Math.PI) / 180

  const x = -(radius * Math.sin(phi) * Math.cos(theta))
  const z = radius * Math.sin(phi) * Math.sin(theta)
  const y = radius * Math.cos(phi)

  return new Vector3(x, y, z)
}

export function createArcCurve(
  start: Vector3,
  end: Vector3,
  radius: number,
  heightFactor = 0.3
) {
  const elevatedRadius = radius * (1 + heightFactor)
  const startDir = start.clone().normalize()
  const endDir = end.clone().normalize()

  const startElevated = startDir.clone().multiplyScalar(elevatedRadius)
  const endElevated = endDir.clone().multiplyScalar(elevatedRadius)

  const axis = startDir.clone().cross(endDir)
  const axisLength = axis.length()
  const angle = startDir.angleTo(endDir)

  const points: Vector3[] = []
  const segments = 32

  if (axisLength < 1e-6) {
    for (let i = 0; i <= segments; i += 1) {
      const t = i / segments
      points.push(startElevated.clone().lerp(endElevated, t))
    }
  } else {
    axis.normalize()
    for (let i = 0; i <= segments; i += 1) {
      const t = i / segments
      const dir = startDir.clone().applyAxisAngle(axis, angle * t)
      points.push(dir.multiplyScalar(elevatedRadius))
    }
  }

  const path = new CurvePath<Vector3>()
  const arcCurve = new CatmullRomCurve3(points, false, 'centripetal')
  const startJoin = new CatmullRomCurve3(
    [start, startElevated, points[Math.min(1, points.length - 1)]],
    false,
    'centripetal'
  )
  const endJoin = new CatmullRomCurve3(
    [
      points[Math.max(points.length - 2, 0)],
      endElevated,
      end,
    ],
    false,
    'centripetal'
  )

  path.add(startJoin)
  path.add(arcCurve)
  path.add(endJoin)

  return path
}
